import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Plus, Bell, Trash2, Edit3, X, Search,
  Globe, Mail as MailIcon, ShieldCheck, Server,
  CheckCircle2, Clock,
} from "lucide-react";

const SERVICE_META = {
  hosting: { label: "Hosting", icon: Globe },
  email:   { label: "Email",   icon: MailIcon },
  ssl:     { label: "SSL",     icon: ShieldCheck },
  server:  { label: "Server",  icon: Server },
};
const SERVICES = ["hosting", "email", "ssl", "server"];

function ReminderModal({ open, initial, customers, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    service_type: "hosting",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    status: "not_billed",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [showCustomerList, setShowCustomerList] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        service_type: initial?.service_type || "hosting",
        customer_name: initial?.customer_name || "",
        customer_email: initial?.customer_email || "",
        customer_phone: initial?.customer_phone || "",
        status: initial?.status || "not_billed",
        notes: initial?.notes || "",
      });
    }
  }, [open, initial]);

  const customerMatches = useMemo(() => {
    const term = form.customer_name.trim().toLowerCase();
    if (!term) return customers.slice(0, 8);
    return customers.filter((c) =>
      (c.customer_name || "").toLowerCase().includes(term)
      || (c.customer_email || "").toLowerCase().includes(term)
    ).slice(0, 8);
  }, [customers, form.customer_name]);

  if (!open) return null;

  const pickCustomer = (c) => {
    setForm((prev) => ({
      ...prev,
      customer_name: c.customer_name || "",
      customer_email: c.customer_email || "",
      customer_phone: c.customer_phone || "",
    }));
    setShowCustomerList(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error("Please select a client");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        service_type: form.service_type,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        status: form.status,
        notes: form.notes.trim(),
      };
      if (isEdit) {
        await api.patch(`/reminders/${initial.id}`, payload);
        toast.success("Reminder updated");
      } else {
        await api.post("/reminders", payload);
        toast.success("Reminder added");
      }
      onSaved();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid="reminder-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Reminders</div>
            <h3 className="font-display font-bold text-lg text-slate-900">
              {isEdit ? "Edit reminder" : "Add reminder"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"
                  data-testid="reminder-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="beacon-label">Service</label>
            <select className="beacon-input" value={form.service_type}
                    data-testid="reminder-service-select"
                    onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
              {SERVICES.map((s) => (
                <option key={s} value={s}>{SERVICE_META[s].label}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="beacon-label">Client</label>
            <input className="beacon-input" placeholder="Start typing a company name…"
                   data-testid="reminder-client-input"
                   value={form.customer_name}
                   onFocus={() => setShowCustomerList(true)}
                   onChange={(e) => { setForm({ ...form, customer_name: e.target.value }); setShowCustomerList(true); }} />
            {showCustomerList && customerMatches.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                   data-testid="reminder-client-suggestions">
                {customerMatches.map((c) => (
                  <button type="button" key={c.customer_name}
                          onClick={() => pickCustomer(c)}
                          data-testid={`reminder-client-pick-${(c.customer_name || "").replace(/\s+/g, "-").toLowerCase()}`}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                    <div className="text-sm font-semibold text-slate-900">{c.customer_name}</div>
                    <div className="text-[11px] text-slate-500">{c.customer_email || "—"}</div>
                  </button>
                ))}
              </div>
            )}
            {form.customer_email && (
              <div className="text-[11px] text-slate-500 mt-1 font-mono">
                {form.customer_email}{form.customer_phone ? ` · ${form.customer_phone}` : ""}
              </div>
            )}
          </div>
          <div>
            <label className="beacon-label">Status</label>
            <div className="grid grid-cols-2 gap-2">
              <label
                data-testid="reminder-status-not-billed"
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${form.status === "not_billed" ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <input type="radio" name="reminder-status" value="not_billed"
                       checked={form.status === "not_billed"}
                       onChange={() => setForm({ ...form, status: "not_billed" })}
                       className="hidden" />
                <Clock className="w-4 h-4" /> Not billed
              </label>
              <label
                data-testid="reminder-status-billed"
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${form.status === "billed" ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <input type="radio" name="reminder-status" value="billed"
                       checked={form.status === "billed"}
                       onChange={() => setForm({ ...form, status: "billed" })}
                       className="hidden" />
                <CheckCircle2 className="w-4 h-4" /> Billed
              </label>
            </div>
          </div>
          <div>
            <label className="beacon-label">Notes for reminder</label>
            <textarea className="beacon-input" rows={4}
                      data-testid="reminder-notes-textarea"
                      placeholder="e.g. Renewal invoice pending — waiting on PO from finance"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}
                  data-testid="reminder-save-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add reminder"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function Reminders() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (service !== "all") params.set("service_type", service);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/reminders${params.toString() ? `?${params}` : ""}`);
      setRows(r.data.reminders || []);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error("Could not load reminders", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [service, statusFilter]);

  const loadCustomers = useCallback(async () => {
    try {
      const r = await api.get("/subscriptions/customers");
      setCustomers(r.data || []);
    } catch (err) {
      console.warn("Could not load subscription customers:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      [r.customer_name, r.customer_email, r.customer_phone, r.notes]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setModalOpen(true); };

  const toggleStatus = async (r) => {
    const next = r.status === "billed" ? "not_billed" : "billed";
    try {
      await api.patch(`/reminders/${r.id}`, { status: next });
      toast.success(next === "billed" ? "Marked as Billed" : "Marked as Not Billed");
      load();
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this reminder for ${r.customer_name}?`)) return;
    try {
      await api.delete(`/reminders/${r.id}`);
      toast.success("Reminder removed");
      load();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Reminders
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Track billing follow-ups per client and service — flip to Billed once invoiced.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openAdd} data-testid="add-reminder-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg h-10">
            <Plus className="w-4 h-4 mr-1.5" /> Add reminder
          </Button>
        )}
      </div>

      {/* Service tabs with counts */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <button
          key="all"
          onClick={() => setService("all")}
          data-testid="reminder-service-tab-all"
          className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all whitespace-nowrap ${
            service === "all" ? "bg-slate-900 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
          }`}
        >
          <Bell className="w-4 h-4" /> All ({counts.total ?? 0})
        </button>
        {SERVICES.map((s) => {
          const Icon = SERVICE_META[s].icon;
          const active = service === s;
          return (
            <button key={s} onClick={() => setService(s)}
                    data-testid={`reminder-service-tab-${s}`}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all whitespace-nowrap ${
                      active ? "bg-slate-900 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}>
              <Icon className="w-4 h-4" />
              {SERVICE_META[s].label} ({counts[s] ?? 0})
            </button>
          );
        })}
      </div>

      {/* Status filter + search */}
      <div className="beacon-card p-3 flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="beacon-input pl-9" placeholder="Search company, email, phone, notes…"
                 data-testid="reminder-search"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="beacon-input w-auto" value={statusFilter}
                data-testid="reminder-status-filter"
                onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="not_billed">Not billed ({counts.not_billed ?? 0})</option>
          <option value="billed">Billed ({counts.billed ?? 0})</option>
        </select>
      </div>

      {/* List */}
      <div className="space-y-2">
        {loading ? (
          <div className="beacon-card p-8 text-center text-sm text-slate-500">Loading reminders…</div>
        ) : filtered.length === 0 ? (
          <div className="beacon-card p-10 text-center">
            <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <div className="font-semibold text-slate-700">No reminders yet</div>
            <div className="text-xs text-slate-500 mt-1">Add one to track billing follow-ups for a client.</div>
          </div>
        ) : (
          filtered.map((r) => {
            const svc = SERVICE_META[r.service_type] || SERVICE_META.hosting;
            const Icon = svc.icon;
            const isBilled = r.status === "billed";
            const tone = isBilled
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200";
            return (
              <div key={r.id} data-testid={`reminder-row-${r.id}`}
                   className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${tone}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                    isBilled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{r.customer_name}</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-white/70 border border-slate-200 rounded px-1.5 py-0.5">{svc.label}</span>
                      <span className={`beacon-badge ${isBilled ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}
                            data-testid={`reminder-status-badge-${r.id}`}>
                        {isBilled ? "Billed" : "Not billed"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 truncate">
                      {r.customer_email || "—"}{r.customer_phone && <span> · <span className="font-mono">{r.customer_phone}</span></span>}
                    </div>
                    {r.notes && (
                      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.notes}</div>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline"
                            onClick={() => toggleStatus(r)}
                            data-testid={`reminder-toggle-${r.id}`}
                            className={isBilled ? "border-amber-300 text-amber-800 hover:bg-amber-50" : "border-emerald-300 text-emerald-800 hover:bg-emerald-50"}>
                      {isBilled ? <><Clock className="w-3.5 h-3.5 mr-1" /> Mark not billed</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark billed</>}
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => openEdit(r)}
                            data-testid={`reminder-edit-${r.id}`}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost"
                            onClick={() => remove(r)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`reminder-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <ReminderModal
        open={modalOpen}
        initial={editing}
        customers={customers}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); load(); }}
      />
    </div>
  );
}
